<template>
    <FilterWrapper
        :count-active-filters="countActiveFilters"
        :default-opened="defaultOpened"
        @filter-visibility-change="handleFilterVisibilityChange"
        @reset="resetFilters"
    >
        <div>
            <CFormAutocomplete
                autoexpand
                :disabled="isLoadingOptions"
                no-results-message="Žiadne výsledky pre zadané kľúčové slová"
                :options="options.keywords"
                placeholder="Kľúčové slová"
                prefix-icon="hashtag"
                :model-value="value.keywords"
                @click="trackInputInteraction('keywords')"
                @update:model-value="update({ keywords: $event })"
            />
            <SuggestedKeywords
                class="mt-2"
                :selected-keywords="value.keywords"
                :suggested-keywords="suggestedKeywords"
                @select-keyword="selectKeyword"
            />
        </div>

        <div class="flex flex-col gap-2 md:flex-row md:gap-4">
            <FilterDropdownNext
                autoexpand
                class="md:flex-1"
                :disabled="isLoadingOptions"
                inline
                :options="options.locations"
                placeholder="Lokalita"
                searchable
                :value="value.locations"
                @click="trackInputInteraction('locations')"
                @input="update({ locations: $event })"
            />

            <FilterDropdownNext
                autoexpand
                class="md:flex-1"
                :disabled="isLoadingOptions || !options.types?.length"
                inline
                :options="options.types"
                placeholder="Typ príležitosti"
                :value="value.types"
                @click="trackInputInteraction('types')"
                @input="update({ types: $event })"
            />
        </div>

        <template #footer>
            <slot />
        </template>
    </FilterWrapper>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { Collections } from '#shared/constants/collections'
import { type optionType } from '#shared/types/dictionary'
import type { OrgId } from '#shared/types/ids'
import { EDUVALUE_KEYWORDS } from '@/constants/suggestedKeywords'
import { useTypesense } from '@/composables/typesense/useTypesense'
import { useExponea } from '@/composables/exponea/useExponea'

export type ForcedFiltersInputTypes = {
    keywords: {
        hidden?: boolean
        values?: string[]
    }
    orgs: {
        hidden?: boolean
        values?: OrgId[]
    }
    locations: {
        hidden?: boolean
        values?: string[]
    }
    types: {
        hidden?: boolean
        values?: Collections[]
    }
}

const props = withDefaults(
    defineProps<{
        value: Record<string, any>
        forcedFilters: ForcedFiltersInputTypes
        indexes?: Collections[]
        defaultOpened?: boolean
    }>(),
    {
        indexes: undefined,
        defaultOpened: false
    }
)

const emit = defineEmits<{
    (e: 'input', value: Record<string, any>): void
    (e: 'filter-visibility-change', isExpanded: boolean): void
}>()

type EduvalueFilterOptions = {
    keywords: string[]
    locations: optionType[]
    types: optionType[]
}

const isLoadingOptions = ref(true)
const options = ref<EduvalueFilterOptions>({
    keywords: [],
    locations: [],
    types: []
})
const suggestedKeywords = ref(EDUVALUE_KEYWORDS)
const { loadFederatedFacets } = useTypesense()
const { trackExponeaEvent } = useExponea()

// Org schema differs from opportunities/users schema (geocity vs geoCity, no visibility field),
// so facets are loaded in two valid collection groups and merged.
const EDUVALUE_ORG_FACET_COLLECTIONS = [
    { collection: 'orgs', filterBy: 'interests:=`eduvalue`' }
]

const EDUVALUE_SEARCH_FACET_COLLECTIONS: Array<{ collection: string; filterBy: string }> = [
    { collection: 'jobs', filterBy: 'visibility:=`published` && topics:=`eduvalue`' },
    { collection: 'projects', filterBy: 'visibility:=`published` && topics:=`eduvalue`' },
    { collection: 'skills', filterBy: 'visibility:=`published` && topics:=`eduvalue`' },
    { collection: 'mentors', filterBy: 'visibility:=`published` && interests:=`eduvalue`' },
    { collection: 'volunteers', filterBy: 'visibility:=`published` && interests:=`eduvalue`' }
]

// Default Slovak cities for location filter
const DEFAULT_LOCATIONS = [
    'Bratislava',
    'Košice',
    'Prešov',
    'Žilina',
    'Banská Bystrica',
    'Nitra',
    'Trnava',
    'Trenčín',
    'Martin',
    'Poprad',
    'Prievidza',
    'Zvolen',
    'Považská Bystrica',
    'Michalovce',
    'Nové Zámky',
    'Spišská Nová Ves',
    'Komárno',
    'Levice',
    'Humenné',
    'Bardejov',
    'Online'
]

const countActiveFilters = computed(() => Object.keys(props.value).length)

onMounted(() => {
    loadOptions()
})

function handleFilterVisibilityChange(isExpanded: boolean) {
    emit('filter-visibility-change', isExpanded)
}

async function getFacets() {
    const [orgFacets, searchFacets] = await Promise.all([
        loadFederatedFacets(EDUVALUE_ORG_FACET_COLLECTIONS, {
            facets: ['geocity', 'interests']
        }),
        loadFederatedFacets(EDUVALUE_SEARCH_FACET_COLLECTIONS, {
            facets: ['geoCity', 'interests', 'topics']
        })
    ])

    const mergedInterests = {
        ...(orgFacets?.interests || {}),
        ...(searchFacets?.interests || {})
    }

    return {
        // Merge valid location fields from both schema variants.
        locations: [
            ...Object.keys(searchFacets?.geoCity || {}),
            ...Object.keys(orgFacets?.geocity || {})
        ],
        interests: Object.keys(mergedInterests),
        topics: Object.keys(searchFacets?.topics || {})
    }
}

function getKeywordsFromFacets(facetValues: string[]): string[] {
    const uniqueKeywords = new Set(facetValues)
    return [...uniqueKeywords]
}

function getLocationsFromFacets(facetValues: string[]): optionType[] {
    const uniqueLocations = new Set(facetValues)
    const sortedLocations = [...uniqueLocations].sort()
    return sortedLocations.map((id) => ({
        id,
        label: id,
        value: id
    }))
}

// Labels for types filter
const TYPE_LABELS: Partial<Record<Collections, string>> = {
    'orgs': 'Organizácie',
    'jobs': 'Pracovné ponuky',
    'projects': 'Dobrovoľnícke príležitosti',
    'skills': 'Vzdelávacie príležitosti',
    'mentors': 'Mentori',
    'volunteers': 'Dobrovoľníci'
}

function getTypeLabel(index: Collections): string {
    const label = TYPE_LABELS[index]
    if (!label) {
        throw new Error(`[EduvalueFilter] Unsupported type index: ${index}`)
    }
    return label
}

function getTypesFromFacets(indexes: Collections[]): optionType[] {
    return indexes.map((id) => ({
        id,
        value: id,
        label: getTypeLabel(id)
    }))
}

async function loadOptions() {
    isLoadingOptions.value = true

    try {
        const facets = await getFacets()

        // Keywords - combine interests and topics from all collections
        const keywords = getKeywordsFromFacets([
            ...facets.interests,
            ...facets.topics,
            ...EDUVALUE_KEYWORDS
        ])

        // Locations - use facets if available, otherwise use defaults
        const facetLocations = facets.locations || []
        const allLocations = facetLocations.length > 0
            ? [...new Set([...facetLocations, ...DEFAULT_LOCATIONS])]
            : DEFAULT_LOCATIONS
        const locations = getLocationsFromFacets(allLocations)

        // Types - use the indexes prop to get available types
        const types = getTypesFromFacets(props.indexes || [])

        options.value = {
            keywords,
            locations,
            types
        }
    } catch (error) {
        console.error('[EduvalueFilter] Failed to load options:', error)
        options.value = {
            keywords: EDUVALUE_KEYWORDS,
            locations: getLocationsFromFacets(DEFAULT_LOCATIONS),
            types: getTypesFromFacets(props.indexes || [])
        }
    }

    isLoadingOptions.value = false
}

function update(change: any) {
    emit('input', { ...props.value, ...change })

    const changeEntry = Object.entries(change)[0]
    if (!changeEntry) return
    const [field, value] = changeEntry

    if (process.client) {
        trackExponeaEvent({
            eventName: 'eduvalue_search',
            userId: '',
            eventProperties: {
                action: 'select',
                field,
                value
            }
        })
    }
}

function resetFilters() {
    emit('input', {})

    if (process.client) {
        trackExponeaEvent({
            eventName: 'eduvalue_search',
            userId: '',
            eventProperties: { action: 'reset' }
        })
    }
}

function selectKeyword(keyword: string) {
    const keywords = props.value.keywords || []
    update({ keywords: [...keywords, keyword] })
}

function trackInputInteraction(field: string) {
    if (process.client) {
        trackExponeaEvent({
            eventName: 'eduvalue_search',
            userId: '',
            eventProperties: { action: 'browse', field }
        })
    }
}
</script>
